import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "layout/Table/Table";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import { decryptSecretKey } from "services/utils";
import { useAssociatedCourseData } from "hooks/RangeDetails";
import { course_details, clearCourseListData, course_list } from "redux/CourseSlice";
import Wrapper from "layout/Wrapper/Wrapper";
import useNotiStack from "hooks/useNotistack";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import HeatMapListSkeleton from "ui/Skeletons/HeatMapListSkeleton";
import TableSkeletonRows from "ui/Skeletons/TableSkeletonRows";
import HeatMapListTableRowItem from "./HeatMapListTableRowItem/HeatMapListTableRowItem";

import styles from "styles/pages/HeatMapList.module.scss";

const HeatMapList = () => {
	const [page, setPage] = useState(1);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [hasMoreItems, setHasMoreItems] = useState(true);
	const [courses, setCourses] = useState([]);
	const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
	const containerRef = useRef(null);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { toastError } = useNotiStack();
	const secret_Key = decryptSecretKey();
	const profileDetails = JSON.parse(localStorage.getItem("profile_data"));
	const courseData = useAssociatedCourseData();
	const id_adminuser = localStorage.getItem("id_adminuser");

	const openEditor = (course, step) => {
		const query = new URLSearchParams({
			range: course?.id_course ?? "",
			step: String(step),
		});
		if (course?.id_courseInt != null) {
			query.set("courseInt", String(course.id_courseInt));
		}
		navigate(`/heatmapeditor?${query.toString()}`);
	};

	const fetchCourseList = useCallback(
		(pageArg = 1, reset = false) => {
			const trimmed = (courseData.searchedCourseName || "").trim();
			const isNumber = /^\d+$/.test(trimmed);
			const isIdCode12 = /^[A-Za-z0-9_-]{12}$/.test(trimmed);

			const userPayload = {
				secretKey: secret_Key,
				userName: profileDetails?.username,
			};

			if (!trimmed) return Promise.resolve();

			if (reset) {
				setPage(1);
				dispatch(clearCourseListData());
				setCourses([]);
			} else {
				setPage(pageArg);
			}

			setIsLoadingMore(true);

			if (isNumber && !isIdCode12) {
				const idInt = parseInt(trimmed, 10);
				dispatch(clearCourseListData());
				setCourses([]);
				return dispatch(
					course_details({
						body: {
							id_adminuser,
							id_courseInt: idInt,
							stateFormat: 4,
							countryFormat: 4,
							detailLevel: 2,
						},
						user: userPayload,
					})
				)
					.then((resp) => {
						if (resp?.payload?.data?.Status === 1) {
							setCourses([resp.payload.data]);
						} else {
							toastError("Course not found");
							setCourses([]);
						}
						setHasMoreItems(false);
					})
					.catch(() => {
						toastError("Course not found");
						setCourses([]);
						setHasMoreItems(false);
					})
					.finally(() => setIsLoadingMore(false));
			}

			if (isIdCode12) {
				dispatch(clearCourseListData());
				setCourses([]);
				return dispatch(
					course_details({
						body: {
							id_adminuser,
							id_course: trimmed,
							stateFormat: 4,
							countryFormat: 4,
							detailLevel: 2,
						},
						user: userPayload,
					})
				)
					.then((resp) => {
						if (resp?.payload?.data?.Status === 1) {
							setCourses([resp.payload.data]);
						} else {
							toastError("Course not found");
							setCourses([]);
						}
						setHasMoreItems(false);
					})
					.catch(() => {
						toastError("Course not found");
						setCourses([]);
						setHasMoreItems(false);
					})
					.finally(() => setIsLoadingMore(false));
			}

			return dispatch(
				course_list({
					body: {
						id_adminuser,
						courseName: trimmed,
						stateFormat: 4,
						page: pageArg,
						resultsPerPage: 100,
					},
					user: userPayload,
				})
			)
				.then((resp) => {
					const fetched = resp?.payload?.data?.courseList || [];
					const total = resp?.payload?.data?.totalCourses || 0;
					setCourses((prev) => {
						const next = reset ? fetched : [...prev, ...fetched];
						setHasMoreItems(pageArg < 5 && next.length < total);
						return next;
					});
				})
				.finally(() => setIsLoadingMore(false));
		},
		[
			courseData.searchedCourseName,
			secret_Key,
			profileDetails?.username,
			id_adminuser,
			dispatch,
			toastError,
		]
	);

	useEffect(() => {
		if (courseData.searchedCourseName) {
			fetchCourseList(1, true);
		} else {
			setCourses([]);
			setPage(1);
			setHasMoreItems(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [courseData.searchedCourseName, courseData.courseSearchTick]);

	const requestSort = (key) => {
		let direction = "asc";
		if (sortConfig.key === key && sortConfig.direction === "asc") {
			direction = "desc";
		}
		setSortConfig({ key, direction });
	};

	const sortedCourseList = useMemo(() => {
		if (courses.length) {
			let sortableItems = [...courses];
			if (sortConfig.key) {
				sortableItems.sort((a, b) => {
					if (a[sortConfig.key] < b[sortConfig.key]) {
						return sortConfig.direction === "asc" ? -1 : 1;
					}
					if (a[sortConfig.key] > b[sortConfig.key]) {
						return sortConfig.direction === "asc" ? 1 : -1;
					}
					return 0;
				});
			}
			return sortableItems;
		}
		return [];
	}, [courses, sortConfig]);

	const handleScroll = useCallback(() => {
		if (!containerRef.current) return;
		const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
		if (
			scrollHeight - scrollTop - clientHeight < 100 &&
			hasMoreItems &&
			!isLoadingMore &&
			page < 5
		) {
			const next = page + 1;
			setPage(next);
			fetchCourseList(next);
		}
	}, [page, hasMoreItems, isLoadingMore, fetchCourseList]);

	const debouncedHandleScroll = useMemo(
		() => debounce(handleScroll, 200),
		[handleScroll]
	);

	useEffect(() => {
		return () => {
			debouncedHandleScroll.cancel();
		};
	}, [debouncedHandleScroll]);

	const isLoading =
		(courseData?.course_list_status === "loading" && courses.length === 0) ||
		(courseData?.course_details_status === "loading" && courses.length === 0);

	return (
		<div className="main_wraper">
			<Wrapper isHeader={true} headerTitle={"HEAT MAP"}>
				<div className={styles.range_sec}>
					<Table title="Courses" addIcon={false} addIconOnClick={() => {}}>
						{isLoading ? (
							<HeatMapListSkeleton />
						) : (
							<>
								{courses.length > 0 ? (
									<div
										className={styles.new_table}
										ref={containerRef}
										onScroll={debouncedHandleScroll}
									>
										<table className={styles.table_bar}>
											<thead>
												<tr style={{ cursor: "pointer" }}>
													<th
														className={styles.id}
														onClick={() => requestSort("id_courseInt")}
													>
														ID
													</th>
													<th
														className={styles.name}
														onClick={() => requestSort("courseName")}
													>
														NAME
													</th>
													<th
														className={styles.city}
														onClick={() => requestSort("city")}
													>
														CITY
													</th>
													<th
														className={styles.state}
														onClick={() => requestSort("stateShort")}
													>
														STATE
													</th>
													<th
														className={styles.country}
														onClick={() => requestSort("countryFull")}
													>
														COUNTRY
													</th>
													<th
														className={styles.state}
														onClick={() => requestSort("id_course")}
													>
														ID COURSE
													</th>
													<th
														className={styles.state}
														onClick={() => requestSort("layoutHoles")}
													>
														LAYOUT
													</th>
													<th
														className={styles.one_meter}
														onClick={() =>
															requestSort("1meterElevationAvailable")
														}
													>
														ONE METER
													</th>
													<th
														className={styles.state}
														onClick={() => requestSort("greenHeatmapAvailable")}
													>
														HEATMAPS
													</th>
													<th className={styles.action}>ACTION</th>
												</tr>
											</thead>
											<tbody>
												{sortedCourseList.map((course) => (
													<HeatMapListTableRowItem
														key={course.id_course}
														course={course}
														onResolutionClick={openEditor}
													/>
												))}
												{hasMoreItems && (
													<TableSkeletonRows rows={3} columns={10} />
												)}
											</tbody>
										</table>
									</div>
								) : courseData.searchedCourseName ? (
									<div className={styles.searchDrivingRange}>
										<SearchOffIcon className={styles.notDataicon} />
										<p className={styles.notDataContent}>No Courses Found</p>
										<p className={styles.dataSearchContent}>
											Please refine your search request
										</p>
									</div>
								) : (
									<div className={styles.searchDrivingRange}>
										<SearchIcon className={styles.notDataicon} />
										<p className={styles.notDataContent}>
											Please Search For Courses
										</p>
									</div>
								)}
							</>
						)}
					</Table>
				</div>
			</Wrapper>
		</div>
	);
};

export default HeatMapList;
